import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getZone, getZoneForStep, getColorCorrectorZone, getContourZone } from "../lib/faceLandmarks";
import {
  classifyFaceShape,
  createShapeSmoother,
} from "../lib/faceShape";
import { classifyEyeShape } from "../lib/eyeShape";
import { saveSnapshot, fetchShadowZones, saveFaceShape, fetchFaceShape, saveEyeShape } from "../lib/api";
import { useStealth } from "../context/StealthContext";
import { stealthSaveSnapshot } from "../lib/stealthDb";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import LiveCoachStepCard from "./LiveCoachStepCard";
import FaceShapeCalibrator from "./FaceShapeCalibrator";
import { useTTS } from "../hooks/useTTS";
import { useVoiceCommands } from "../hooks/useVoiceCommands";
import { useDemoMode } from "../context/DemoModeContext";
import { getTechniqueMotionGuide } from "./TechniqueSchematic";
import { drawZoneOnCanvas } from "./liveCoach/drawing";
import LiveCoachTopBar from "./liveCoach/LiveCoachTopBar";
import LiveCoachStatusOverlay from "./liveCoach/LiveCoachStatusOverlay";
import LiveCoachVoiceFlash from "./liveCoach/LiveCoachVoiceFlash";
import LiveCoachFaceShapeBadge from "./liveCoach/LiveCoachFaceShapeBadge";
import MirrorWakeWordHint from "./liveCoach/MirrorWakeWordHint";
import PrivacyToast from "./liveCoach/PrivacyToast";
import AskTrouveOverlay from "./AskTrouveOverlay";
import LiveCoachEncouragement, { pickRandomEncouragementKey } from "./LiveCoachEncouragement";

/**
 * Learn with Trouvé — opens a full-screen overlay with the user's
 * camera, runs MediaPipe face landmark detection, and renders soft
 * glowing dots on the user's face indicating where to apply for the
 * active step. (Internally still named "LiveCoach" so call sites +
 * tests stay stable — the user-facing copy says "Learn with Trouvé".)
 */
// Serve MediaPipe WASM from our own CloudFront origin. The previous jsDelivr
// dependency left the mirror stuck on its first loading screen for visitors
// whose browser, network, or privacy tooling blocked that third-party CDN.
// `npm run copy:mediapipe` copies the exact package version into public/ before
// every local start and production build, keeping JS and WASM versions aligned.
const MEDIAPIPE_WASM = `${process.env.PUBLIC_URL || ""}/vendor/mediapipe/tasks-vision/wasm`;
// Face landmarker model — bundled locally via `public/models/` so the
// Capacitor build serves it from the app bundle (file://) instead of
// the slow googleapis CDN. Saves ~3.6MB on every cold start.
// Browsers also benefit (PUBLIC_URL resolves to the deployed origin).
const FACE_MODEL_URL = `${process.env.PUBLIC_URL || ""}/models/face_landmarker.task`;

// ─── Desktop-safety tuning (iter 76, Jun 24 2026) ────────────────────
// Founder report: AR Mirror crashes desktop browsers (Chrome/Safari).
// Root cause: unbounded resolution + 60 FPS RAF + uncapped DPR canvas
// + GPU delegate on Intel iGPUs created GPU/CPU pressure that
// occasionally took down the whole tab. Mobile path was unaffected
// because device cameras are inherently smaller + iOS WebKit clamps
// frame rate hard.
//
// Strategy: detect "desktop-ish" via the absence of mobile UA markers
// AND the absence of a coarse pointer (touch). On desktop:
//   • Cap camera resolution to 640×480 ideal / 1280×720 max (was
//     720×960 ideal, no max).
//   • Cap frame rate to 24 FPS ideal / 30 max (was unset → up to 60).
//   • Throttle MediaPipe inference to ~24 ticks/s even if RAF runs
//     at 60-144Hz on high-refresh-rate monitors.
//   • Cap canvas DPR to 2 (Retina is fine; 5K/Pro Display XDR @ 3x
//     was 3.4M pixels per frame on a 4K monitor — pure GPU pressure).
//   • Pause the loop when the tab loses visibility (RAF still fires
//     in iframes / backgrounded tabs on some platforms).
//   • GPU delegate with explicit CPU fallback if GPU init throws —
//     known MediaPipe issue on older Intel iGPU drivers.
const _isMobileUA = () =>
  typeof navigator !== "undefined" &&
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
const _hasCoarsePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;
const _isDesktopEnv = () => !_isMobileUA() && !_hasCoarsePointer();

const _videoConstraintsFor = (desktop) =>
  desktop
    ? {
        facingMode: "user",
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        frameRate: { ideal: 24, max: 30 },
      }
    : {
        facingMode: "user",
        width: { ideal: 720 },
        height: { ideal: 960 },
      };

// Minimum ms between MediaPipe inferences. 1000/24 ≈ 41.7ms gives us
// a steady ~24 FPS detection cadence on desktop. Mobile keeps the
// previous fully-driven-by-RAF behaviour because device cameras
// rarely exceed 30 FPS to begin with.
const DESKTOP_MIN_INFERENCE_MS = 1000 / 24;

const MODEL_START_TIMEOUT_MS = 45_000;
const CAMERA_START_TIMEOUT_MS = 30_000;
const VIDEO_START_TIMEOUT_MS = 12_000;

const _withTimeout = (promise, timeoutMs, name, message) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(message);
      error.name = name;
      reject(error);
    }, timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// Cap device-pixel-ratio for canvas buffer sizing — see comment above.
const _safeDpr = (desktop) =>
  Math.min(
    (typeof window !== "undefined" && window.devicePixelRatio) || 1,
    desktop ? 2 : 3,
  );

const LiveCoach = ({
  steps,
  initialStepIdx = 0,
  onClose,
  onStepDone,
  routineId,
  onSnapshotSaved,
  closing,
  showAskTrouveAlways = false,
  saveSnapshotOverride,
  snapshotSavedMessage,
  ephemeral = false,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastVideoTime = useRef(-1);
  // P5 — fire `face_detected` exactly once per session, the first time
  // MediaPipe surfaces a face. Reset on session end via component unmount
  // (the ref is re-created with the next mount).
  const faceDetectedFiredRef = useRef(false);

  const { stealth } = useStealth();
  const tts = useTTS({ persist: !ephemeral, remote: !ephemeral });
  const [stepIdx, setStepIdx] = useState(initialStepIdx);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [initAttempt, setInitAttempt] = useState(0);
  // Sub-stage during phase==="loading" — surfaces what's happening so
  // the cold-start blank moment becomes a 3-beat narrative instead.
  // model → camera → warming → (phase=ready hides overlay)
  const [loadingStage, setLoadingStage] = useState("model");
  const [errorMsg, setErrorMsg] = useState("");
  const [doneSet, setDoneSet] = useState(new Set());
  // Task 6 — positive reinforcement. When the user marks a step done,
  // ~every other completion we show a short Trouvé encouragement
  // ("Nice." / "We're getting somewhere.") that also plays in voice
  // if TTS is enabled. `encouragementKey` is the i18n key for the
  // currently-visible line, or null when nothing is showing.
  // Frequency-controlled with a counter so we don't pile encouragement
  // on top of every single check — we target ~1 out of every 2
  // completions, plus the final step.
  const [encouragementKey, setEncouragementKey] = useState(null);
  const completionCounter = useRef(0);
  const [savingSnap, setSavingSnap] = useState(false);
  const [flash, setFlash] = useState(false);
  const step = steps?.[stepIdx];

  // User-saved custom shadow zones (color_corrector step uses these
  // when present). Loaded once on mount; falls back to default
  // full-lower-face polygon when the user hasn't customised.
  const [userShadowZones, setUserShadowZones] = useState(null);
  useEffect(() => {
    if (ephemeral) {
      setUserShadowZones([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchShadowZones();
        if (!cancelled) setUserShadowZones(data?.zones || []);
      } catch {
        // Not authed / no profile yet → stay on the default polygon.
        if (!cancelled) setUserShadowZones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ephemeral]);

  // Live face-shape classification — drives the contour step's
  // stroke placement. Starts null; flips to "oval" / "round" / etc.
  // after the smoother has locked on (≈6 frames of the same result).
  // Also warm-started from the user's saved face-shape profile (if
  // any) so the first-open flicker is eliminated on return visits.
  //
  // `faceShapeSource` is "detected" when the classifier set it, and
  // "user_set" when the user calibrated via the badge picker. Once
  // user_set, the live classifier stops overwriting — the user's
  // explicit choice wins until they hit "Change my shape".
  const [faceShape, setFaceShape] = useState(null);
  const [faceShapeSource, setFaceShapeSource] = useState(null);
  const [calibratorOpen, setCalibratorOpen] = useState(false);
  const shapeSmootherRef = useRef(createShapeSmoother());
  const shapeFrameRef = useRef(0);
  const faceShapeRef = useRef(null);
  const faceShapeSourceRef = useRef(null);
  // Eye-shape classifier state — runs on the SAME landmark stream as
  // face-shape but on a different cadence (every 45 frames vs 15) so
  // the two never race on the same RAF tick. Source-pinning matches
  // face-shape: "user_set" wins, "landmark" advisory.
  const eyeShapeRef = useRef(null);
  const eyeShapeSourceRef = useRef(null);
  const eyeShapeFrameRef = useRef(0);
  useEffect(() => {
    if (ephemeral) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { fetchEyeShape: _fetchEye } = await import("../lib/api");
        const r = await _fetchEye();
        if (!cancelled && r?.shape) {
          eyeShapeRef.current = r.shape;
          eyeShapeSourceRef.current = r.source || null;
        }
      } catch {
        /* unauthed or no saved eye_shape → live detection takes over */
      }
    })();
    return () => { cancelled = true; };
  }, [ephemeral]);
  useEffect(() => {
    if (ephemeral) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { shape, source } = await fetchFaceShape();
        if (!cancelled && shape) {
          faceShapeRef.current = shape;
          faceShapeSourceRef.current = source || "detected";
          setFaceShape(shape);
          setFaceShapeSource(source || "detected");
          // Seed the live smoother so the AR mirror has the right
          // contour zone on frame 1 — instead of starting from null
          // and forcing the user to stare at the wrong contour for
          // 3-5 seconds until the live classifier converges. A few
          // disagreeing live samples will quickly override the seed
          // if their face shape has actually changed.
          shapeSmootherRef.current?.seed?.(shape);
        }
      } catch {
        /* unauthed / no saved shape → stays null, live detection takes over */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ephemeral]);

  // For the color-corrector step, swap in the user's saved shadow
  // Zone resolution. For color-correction we re-shape based on the
  // user's selected shadow zones. For contour, swap in the
  // face-shape-tuned stroke set. Every other category uses the
  // where-aware resolver so technique steps like "in the crease"
  // or "outer V" highlight the narrower patch the spoken line
  // describes — not the whole lid. The fallback when no `where`
  // matches a sub-zone keyword is the original full-category zone.
  let zone;
  if (step?.category === "color_corrector") {
    zone = getColorCorrectorZone(userShadowZones);
  } else if (step?.category === "contour") {
    zone = getContourZone(faceShape);
  } else {
    zone = getZoneForStep(step?.category, step?.where, step?.title, step?.what);
  }

  // Speak the current step's coaching when:
  //   1. The mirror has finished loading (phase === "ready"), AND
  //   2. The active step changes (stepIdx OR step?.title), AND
  //   3. TTS is enabled in user prefs.
  //
  // What we speak (and why):
  //   1. The step number + title — orients the user to "Step 3. Kiss
  //      the cheeks with blush."
  //   2. The LLM's `where` text from the routine itself — this is the
  //      single source of truth the AR mirror also reads to highlight
  //      the zone. Speaking the SAME text the visual is drawing means
  //      voice and image can never desync (the previous version spoke
  //      a generic per-category motion guide that often described a
  //      different patch than the LLM had in mind — see feedback
  //      "Image doesn't match verbal instructions").
  //   3. As a tail polish, the short generic technique caption — gives
  //      a quick motion cue (e.g. "stipple, never sweep") without
  //      overriding the LLM's specific placement guidance.
  //
  // First time the mirror opens we prepend a warm, celebratory greeting
  // ("Okay gorgeous, here we go — first up, your eyes.") so the voice's
  // personality lands before any technical instruction. Tracked with a
  // ref so it fires exactly once per LiveCoach mount.
  const hasWarmedUpRef = useRef(false);
  useEffect(() => {
    if (phase !== "ready" || !step || !tts.enabled) return;
    const guide = getTechniqueMotionGuide(step.category);
    // Prefer the LLM's specific `where` text so the spoken patch
    // matches the highlighted patch exactly. Fall back to the
    // generic technique caption only when `where` is missing.
    const specific = (step.where || "").trim();
    const tail = specific ? guide.caption : (guide.steps || []).slice(0, 2).join(". ");
    const stepLines = [
      `Step ${stepIdx + 1}. ${step.title}.`,
      specific || guide.caption,
      tail,
    ]
      .filter(Boolean)
      .join(" ");

    let utterance = stepLines;
    if (!hasWarmedUpRef.current) {
      hasWarmedUpRef.current = true;
      const zoneLabel = (zone?.label || "your face").toLowerCase();
      // Trouvé's first-ever introduction — plays exactly once per
      // device per browser. Gated on a localStorage flag (versioned
      // so we can refresh the intro copy in future). After this
      // first-time line, returning users hear the regular varied
      // openers below.
      const introKey = "trubeaute.trouve.intro_played.v1";
      let firstTimeIntro = null;
      try {
        if (
          typeof window !== "undefined" &&
          window.localStorage &&
          !window.localStorage.getItem(introKey)
        ) {
          firstTimeIntro =
            "Hi gorgeous. I'm Trouvé — your personal drag mom. " +
            "Take your time, I've got you.";
          window.localStorage.setItem(introKey, "1");
          track("trouve_intro_played");
        }
      } catch {
        /* localStorage unavailable (private mode etc.) — silently skip */
      }

      // Slightly varied openers so a returning user doesn't hear the
      // exact same phrase every time — but always warm, always ends
      // with the natural transition into the first step's category.
      const openers = [
        `Okay gorgeous, here we go. First up, ${zoneLabel}.`,
        `Hi love. Let's get you glowing — starting with ${zoneLabel}.`,
        `Alright, beautiful. Take a breath… and we'll start with ${zoneLabel}.`,
      ];
      const opener = openers[Math.floor(Math.random() * openers.length)];
      utterance = firstTimeIntro
        ? `${firstTimeIntro} ${opener} ${stepLines}`
        : `${opener} ${stepLines}`;
    }

    tts.speak(utterance);
    // `voice_tip_played` is the TTS-specific signal — it only fires
    // when audio actually goes out. The visual-tip-shown event is
    // emitted from a SEPARATE effect below so it fires even when TTS
    // is muted or unavailable (and even in text-only fallback mode).
    if (tts.enabled) {
      trackFP("voice_tip_played", { step_idx: stepIdx, voice: "nova" });
    }

    // ── Prefetch the next step's audio in the background ─────────────
    // Why: each step change is a new TTS request, and the first time
    // a given line is spoken it costs a full network round-trip
    // (~1-3s on WiFi, much worse on cellular). The blob cache only
    // hits for IDENTICAL repeated text in the same session — which
    // never happens across step changes because each step's
    // title+where text is unique. Result: every advance felt sluggish.
    //
    // The fix is dirt simple: while the current step is playing,
    // pre-load step N+1 (and N-1, in case the user backs up) so the
    // audio is already sitting in the blob cache by the time they
    // tap Next or say "hey trouvé next". We compose each preview
    // utterance with the SAME shape as the live one so the cache key
    // matches.
    //
    // The first step (stepIdx === 0) has no opener prepended on the
    // prefetch — the warm-up greeting only fires once per mount,
    // controlled by hasWarmedUpRef. Prefetching the un-greeted form
    // would be a cache miss anyway, so we just skip the greeting.
    const composeUtteranceFor = (i) => {
      if (i < 0 || i >= steps.length) return null;
      const s = steps[i];
      if (!s) return null;
      const g = getTechniqueMotionGuide(s.category);
      const w = (s.where || "").trim();
      const t2 = w ? g.caption : (g.steps || []).slice(0, 2).join(". ");
      return [
        `Step ${i + 1}. ${s.title}.`,
        w || g.caption,
        t2,
      ]
        .filter(Boolean)
        .join(" ");
    };
    if (typeof tts.prefetch === "function") {
      const nextUtter = composeUtteranceFor(stepIdx + 1);
      const prevUtter = composeUtteranceFor(stepIdx - 1);
      if (nextUtter) tts.prefetch(nextUtter);
      if (prevUtter) tts.prefetch(prevUtter);
    }
    // We deliberately do not depend on `tts.speak` (it's stable from
    // useCallback) to avoid double-speaking on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stepIdx, step?.title, step?.category, step?.where, tts.enabled]);

  // The animation loop is registered once (empty deps) but needs to read
  // the *current* zone whenever the user advances steps. Without this ref,
  // the loop's closure captures the initial zone and every subsequent step
  // renders with the same shape on the user's face.
  const zoneRef = useRef(zone);
  useEffect(() => {
    zoneRef.current = zone;
  }, [zone]);

  // ---------- Camera + MediaPipe init ----------
  // Detect once per mount so all downstream code uses the same env
  // class (avoids the matchMedia subscription overhead).
  const isDesktopEnvRef = useRef(_isDesktopEnv());
  // Last successful MediaPipe inference timestamp (perf.now()). Used
  // to throttle desktop down to ~24 FPS — see DESKTOP_MIN_INFERENCE_MS.
  const lastInferenceMsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let attemptActive = true;

    // P5 first-party event: session starts the moment LiveCoach mounts.
    // Fires regardless of whether camera/MediaPipe succeed — that's
    // what `live_coach_started` is supposed to capture (intent), with
    // `camera_permission_*` and `face_detected` measuring how far the
    // session actually got.
    trackFP("live_coach_started", {
      routine_id: routineId || null,
      step_count: steps?.length || 0,
    });

    const init = async () => {
      try {
        // 1. MediaPipe WASM + face-landmark model — kicked off FIRST so
        // the slow CDN/local fetch runs in parallel with the camera
        // permission prompt. On cold start, this is the longest beat.
        setLoadingStage("model");
        const rawMpPromise = (async () => {
          const { FilesetResolver, FaceLandmarker } = await import(
            "@mediapipe/tasks-vision"
          );
          const fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);
          // GPU delegate first — fast path. If it throws (known issue
          // on older Intel iGPU drivers, certain Linux Mesa versions,
          // and some headless contexts), retry with CPU delegate so
          // the mirror still works, just slower. CPU stays at ≥15 FPS
          // on most desktops for a single-face VIDEO running-mode
          // pipeline — slower than GPU but never crashes.
          try {
            return await FaceLandmarker.createFromOptions(fileset, {
              baseOptions: {
                modelAssetPath: FACE_MODEL_URL,
                delegate: "GPU",
              },
              runningMode: "VIDEO",
              numFaces: 1,
              outputFaceBlendshapes: false,
            });
          } catch (gpuErr) {
            // eslint-disable-next-line no-console
            console.warn(
              "[LiveCoach] MediaPipe GPU delegate failed, falling back to CPU",
              gpuErr,
            );
            trackFP("live_coach_gpu_fallback", {
              reason: String(gpuErr?.message || gpuErr).slice(0, 120),
            });
            return await FaceLandmarker.createFromOptions(fileset, {
              baseOptions: {
                modelAssetPath: FACE_MODEL_URL,
                delegate: "CPU",
              },
              runningMode: "VIDEO",
              numFaces: 1,
              outputFaceBlendshapes: false,
            });
          }
        })();
        rawMpPromise.then((landmarker) => {
          if (!attemptActive || cancelled) {
            try { landmarker.close(); } catch { /* already closed */ }
          }
        }).catch(() => {});
        const mpPromise = _withTimeout(
          rawMpPromise,
          MODEL_START_TIMEOUT_MS,
          "ModelStartTimeoutError",
          "Face tracking took too long to start.",
        );

        // 2. Camera permission + stream (the user may pause on the
        // browser permission prompt — model keeps loading meanwhile).
        // Desktop gets a lower-resolution, frame-rate-capped stream
        // so the GPU isn't asked to churn through 1080p@60fps from a
        // built-in webcam (which is what some Macs / Windows laptops
        // default to, and which is what crashed the browser).
        setLoadingStage("camera");
        if (!navigator.mediaDevices?.getUserMedia) {
          const unsupported = new Error("Live camera is unavailable in this browser.");
          unsupported.name = "CameraUnsupportedError";
          throw unsupported;
        }
        const rawCameraPromise = navigator.mediaDevices.getUserMedia({
          video: _videoConstraintsFor(isDesktopEnvRef.current),
          audio: false,
        });
        rawCameraPromise.then((lateStream) => {
          if (!attemptActive || cancelled) {
            lateStream.getTracks().forEach((track) => {
              try { track.stop(); } catch { /* track already ended */ }
            });
          }
        }).catch(() => {});
        const stream = await _withTimeout(
          rawCameraPromise,
          CAMERA_START_TIMEOUT_MS,
          "CameraStartTimeoutError",
          "The camera permission request timed out.",
        );
        // P5: permission granted — only one of granted/denied ever fires
        // per session so funnel counts add up.
        trackFP("camera_permission_granted");
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await _withTimeout(
          video.play(),
          VIDEO_START_TIMEOUT_MS,
          "VideoStartTimeoutError",
          "The camera opened but the video feed did not start.",
        );

        // 3. Wait for the MediaPipe model to finish (no-op if it
        // already did while the camera permission was being granted).
        setLoadingStage("warming");
        const landmarker = await mpPromise;
        if (cancelled) {
          landmarker.close();
          return;
        }
        landmarkerRef.current = landmarker;
        setPhase("ready");
        startLoop();
      } catch (e) {
        attemptActive = false;
        // eslint-disable-next-line no-console
        console.error("LiveCoach init failed", e);
        // P5: tag permission denials separately so we can distinguish
        // "user said no" from "MediaPipe / browser blew up".
        if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
          trackFP("camera_permission_denied", { reason: e.name });
        } else {
          trackFP("live_coach_init_failed", {
            error: String(e?.name || e?.message || e).slice(0, 120),
          });
        }
        const message = (() => {
          if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
            return "Please allow camera access in your browser settings, then try again.";
          }
          if (e?.name === "CameraStartTimeoutError") {
            return "The camera permission request timed out. Check for a permission prompt, then try again.";
          }
          if (e?.name === "CameraUnsupportedError") {
            return "This browser cannot provide a live camera. Try Safari or Chrome, or continue with the step guide.";
          }
          if (e?.name === "ModelStartTimeoutError") {
            return "Face tracking took too long to load. Check your connection, then try again.";
          }
          if (e?.name === "VideoStartTimeoutError") {
            return "The camera opened but its video feed did not start. Close other camera apps, then try again.";
          }
          return "We couldn't open the mirror right now. Try again or check camera permissions.";
        })();
        setErrorMsg(message);
        setPhase("error");
      }
    };

    init();

    // Pause the RAF inference loop whenever the tab loses visibility.
    // Without this, RAF keeps firing in some browsers (especially when
    // the tab is in a non-visible workspace) and the GPU keeps doing
    // landmark inference on a stale frame — burning cycles for nothing
    // and contributing to the desktop crash class. Resumed on visible.
    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      } else if (
        landmarkerRef.current &&
        streamRef.current &&
        videoRef.current &&
        !rafRef.current
      ) {
        startLoop();
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      cancelled = true;
      attemptActive = false;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (landmarkerRef.current) {
        try { landmarkerRef.current.close(); } catch { /* tasks-vision close can throw on already-closed */ }
        landmarkerRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* track already ended */ }
        });
        streamRef.current = null;
      }
      // Explicitly detach the stream from the video element. Without
      // this, some browsers (Safari on Mac in particular) hold onto
      // the underlying GPU texture for the lifetime of the <video>
      // node, which can compound the crash class if the user opens +
      // closes the mirror multiple times in one session.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- videoRef.current is intentionally read at cleanup time; the ref is module-stable for the lifetime of this mount.
      const v = videoRef.current;
      if (v) {
        try { v.pause(); } catch { /* video already paused */ }
        try { v.srcObject = null; } catch { /* srcObject setter unavailable */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAttempt]);

  // ---------- Draw immersive zone overlay ----------
  // Declared BEFORE `startLoop` so the useCallback deps can reference
  // it without hitting the JS Temporal Dead Zone. drawZone only reads
  // from refs (zoneRef) + landmark args, so its deps array is `[]` and
  // its identity is stable across renders.
  const drawZone = useCallback(
    (ctx, landmarks, fit) => {
      // Read the *current* zone via ref so per-step changes take effect
      // even though the rAF loop captured this callback at init time.
      drawZoneOnCanvas(ctx, landmarks, fit, zoneRef.current);
    },
    []
  );

  // ---------- Animation loop ----------
  const startLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d");
    const isDesktop = isDesktopEnvRef.current;

    const tick = () => {
      const landmarker = landmarkerRef.current;
      if (!landmarker || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Desktop throttle: even though RAF fires at 60-144Hz on modern
      // monitors and `video.currentTime` advances every camera frame,
      // we cap MediaPipe inference to ~24 ticks/s. This is the single
      // biggest CPU/GPU reduction on the desktop crash class —
      // unthrottled, a 60Hz camera + 144Hz monitor was doing >100
      // landmark detections/sec on the user's webcam stream, which
      // is what set Chrome's GPU process on fire. Mobile keeps the
      // full RAF cadence because mobile cameras rarely exceed 30 FPS
      // and the device thermal envelope handles it fine.
      const now = performance.now();
      if (isDesktop && now - lastInferenceMsRef.current < DESKTOP_MIN_INFERENCE_MS) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Sync canvas to its DISPLAYED size (the parent box). The video uses
      // `object-fit: cover`, so the CSS size of the box is what's visible
      // — sizing the canvas buffer to that (× DPR) keeps dot positions
      // accurate after the cover-crop transform we apply during draw.
      // DPR is capped per environment via _safeDpr — 5K monitors at 3x
      // would otherwise produce 3.4M-pixel canvas buffers per frame.
      const dpr = _safeDpr(isDesktop);
      const rect = canvas.getBoundingClientRect();
      const boxW = Math.max(1, Math.round(rect.width));
      const boxH = Math.max(1, Math.round(rect.height));
      const wantW = boxW * dpr;
      const wantH = boxH * dpr;
      if (canvas.width !== wantW || canvas.height !== wantH) {
        canvas.width = wantW;
        canvas.height = wantH;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // 1 unit = 1 CSS px
      ctx.clearRect(0, 0, boxW, boxH);

      const t = performance.now();
      if (video.currentTime !== lastVideoTime.current) {
        lastVideoTime.current = video.currentTime;
        lastInferenceMsRef.current = now;
        const result = landmarker.detectForVideo(video, t);
        const lm = result.faceLandmarks?.[0];
        if (lm) {
          // P5 funnel: fire `face_detected` exactly once per session,
          // the first time MediaPipe surfaces a face. This is the key
          // "live coaching actually started" signal — without it the
          // user is staring at a black screen.
          if (!faceDetectedFiredRef.current) {
            faceDetectedFiredRef.current = true;
            trackFP("face_detected");
          }
          // Compute the cover-fit transform from raw video → displayed box.
          // (The video element renders with object-fit: cover, so we replicate
          // exactly the same scale + center-crop offsets when placing dots.)
          const vw = video.videoWidth || boxW;
          const vh = video.videoHeight || boxH;
          const s = Math.max(boxW / vw, boxH / vh);
          const offX = (vw * s - boxW) / 2;
          const offY = (vh * s - boxH) / 2;
          drawZone(ctx, lm, { vw, vh, s, offX, offY });

          // Classify face shape every ~15 frames (≈ twice per second
          // at 30fps). The smoother buffers the last 10 results and
          // only locks on when the majority agrees — this kills the
          // flicker you'd otherwise get on borderline faces.
          shapeFrameRef.current = (shapeFrameRef.current + 1) % 15;
          if (shapeFrameRef.current === 0) {
            // Only let the live classifier write when the user hasn't
            // explicitly calibrated. user_set pins the shape until the
            // user changes it from the badge picker.
            if (faceShapeSourceRef.current !== "user_set") {
              const result2 = classifyFaceShape(lm);
              const stable = shapeSmootherRef.current.push(result2);
              if (stable && stable !== faceShapeRef.current) {
                faceShapeRef.current = stable;
                faceShapeSourceRef.current = "detected";
                setFaceShape(stable);
                setFaceShapeSource("detected");
                // Fire-and-forget; backend silently drops detected
                // writes when a user_set value already exists.
                if (!ephemeral) saveFaceShape(stable, "detected").catch(() => {});
                track("face_shape_detected", { shape: stable });
              }
            }
          }
          // Eye-shape classification — runs every 45 frames (~1.5s
          // at 30fps), staggered so it never lands on the same RAF
          // tick as face-shape. Source-pinning: "user_set" wins; we
          // only post advisory "landmark" writes on medium / high
          // confidence so a single noisy frame can't flip the chip.
          eyeShapeFrameRef.current = (eyeShapeFrameRef.current + 1) % 45;
          if (eyeShapeFrameRef.current === 0
              && eyeShapeSourceRef.current !== "user_set") {
            const eyeResult = classifyEyeShape(lm);
            if (
              eyeResult?.shape
              && (eyeResult.confidence === "high"
                  || eyeResult.confidence === "medium")
              && eyeResult.shape !== eyeShapeRef.current
            ) {
              eyeShapeRef.current = eyeResult.shape;
              eyeShapeSourceRef.current = "landmark";
              // Fire-and-forget — backend rejects "landmark" writes
              // when a user_set value already exists, so a stale
              // closure can't stomp the user's pin.
              if (!ephemeral) saveEyeShape(eyeResult.shape, "landmark").catch(() => {});
              track("eye_shape_detected_landmark", {
                shape: eyeResult.shape,
                confidence: eyeResult.confidence,
              });
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [drawZone, ephemeral]);

  // ---------- Actions ----------
  const captureSnapshot = useCallback(async () => {
    if (savingSnap || !videoRef.current || !canvasRef.current || !routineId) return;
    setSavingSnap(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 350);

    try {
      const video = videoRef.current;
      const overlay = canvasRef.current;
      const vw = video.videoWidth || 720;
      const vh = video.videoHeight || 1280;
      const rect = overlay.getBoundingClientRect();
      const boxW = Math.max(1, Math.round(rect.width));
      const boxH = Math.max(1, Math.round(rect.height));
      const sCover = Math.max(boxW / vw, boxH / vh);
      const drawnW = vw * sCover;
      const drawnH = vh * sCover;
      const dx = -(drawnW - boxW) / 2;
      const dy = -(drawnH - boxH) / 2;

      // Compose what the user actually sees: video object-fit: cover'd into
      // the displayed box, mirrored to match the on-screen flip, with the
      // landmark overlay drawn on top at its native (already-aligned) scale.
      const out = document.createElement("canvas");
      out.width = boxW;
      out.height = boxH;
      const ctx = out.getContext("2d");
      ctx.save();
      ctx.translate(boxW, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, dx, dy, drawnW, drawnH);
      // Overlay canvas backing buffer is boxW*dpr × boxH*dpr — drawImage
      // scales it back down to boxW × boxH naturally.
      ctx.drawImage(overlay, 0, 0, boxW, boxH);
      ctx.restore();

      const dataUrl = out.toDataURL("image/jpeg", 0.85);
      const base64 = dataUrl.split(",")[1] || "";

      const payload = {
        routine_id: routineId,
        step_idx: stepIdx,
        step_title: step?.title || `Step ${stepIdx + 1}`,
        mime_type: "image/jpeg",
        image_base64: base64,
      };

      const saved = saveSnapshotOverride
        ? await saveSnapshotOverride(payload)
        : stealth
          ? await stealthSaveSnapshot(payload)
          : await saveSnapshot(payload);

      onSnapshotSaved?.(saved);
      track("learn_with_trouve_snapshot_captured", { step_idx: stepIdx });
      toast.success(
        snapshotSavedMessage ||
          (stealth ? "Saved to this device only." : "Saved to your glow journal."),
      );
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save that moment. Try again?");
    } finally {
      setSavingSnap(false);
    }
  }, [
    savingSnap,
    routineId,
    stepIdx,
    step,
    stealth,
    onSnapshotSaved,
    saveSnapshotOverride,
    snapshotSavedMessage,
  ]);

  const goNext = () => {
    setDoneSet((prev) => new Set([...prev, stepIdx]));
    onStepDone?.(stepIdx);
    if (stepIdx < steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else if (closing && tts.enabled && !signoffPlayedRef.current) {
      // User tapped "Next step" on the final step — same closing
      // moment as marking-done, deliver Trouvé's sign-off in voice.
      signoffPlayedRef.current = true;
      setTimeout(() => {
        const spoken = closing.replace(/\n+/g, ". ");
        tts.speak(spoken);
        track("trouve_signoff_spoken");
      }, 400);
    }
  };
  const goPrev = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };
  const toggleDone = () => {
    setDoneSet((prev) => {
      const next = new Set(prev);
      if (next.has(stepIdx)) {
        next.delete(stepIdx);
      } else {
        next.add(stepIdx);
        onStepDone?.(stepIdx);

        // Task 6 — positive reinforcement. Show a Trouvé encouragement
        // every other completion (counter % 2 === 0) plus the final
        // step. Tucked here so it fires AFTER the doneSet update, so
        // the check-pill animates and Trouvé's praise arrives together.
        completionCounter.current += 1;
        const isFinalStep = stepIdx === steps.length - 1;
        if (completionCounter.current % 2 === 0 || isFinalStep) {
          setEncouragementKey(pickRandomEncouragementKey());
        }

        // Auto-advance to the next step after marking done. Small
        // delay (350ms) so the user sees the check pill fill in
        // before the card transitions. Skipped on the final step —
        // we want users to land on the finish/celebrate moment.
        if (stepIdx < steps.length - 1) {
          setTimeout(() => {
            // Re-check current index inside the timeout in case the
            // user already advanced manually before the timer fired.
            setStepIdx((idx) => (idx === stepIdx ? idx + 1 : idx));
            track("learn_with_trouve_step_advanced", { source: "auto_after_done" });
          }, 350);
        } else if (
          closing &&
          tts.enabled &&
          !signoffPlayedRef.current
        ) {
          // Final step just got marked done — speak Trouvé's closing
          // sign-off so the persona lands the goodbye in audio too.
          // Brief delay lets the check pill animation breathe before
          // the warm voice kicks in. Guarded by a ref so flicking the
          // toggle off+on doesn't re-trigger the speech mid-playback.
          signoffPlayedRef.current = true;
          setTimeout(() => {
            // Replace the literal "\n" with a soft pause so the TTS
            // engine takes a beat before the "xo, Trouvé" sign-off,
            // matching the visual line break on the routine page.
            const spoken = closing.replace(/\n+/g, ". ");
            tts.speak(spoken);
            track("trouve_signoff_spoken");
          }, 600);
        }
      }
      return next;
    });
  };

  // Final-step sign-off plays exactly once per mirror session. Reset
  // implicitly on unmount because the ref dies with the component.
  const signoffPlayedRef = useRef(false);

  // ── Voice commands ("Hey Trouvé, next step") ─────────────────
  // Persisted preference — once a user opts in we leave the mic on
  // across sessions. localStorage key versioned so we can change the
  // default later without surprising existing users.
  const VOICE_PREF_KEY = "trubeaute.trouve.voice_commands.v1";
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (ephemeral) return false;
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(VOICE_PREF_KEY) === "1"; } catch { return false; }
  });
  const [voiceFlash, setVoiceFlash] = useState(null); // {intent, at}

  // Demo mode forces voice commands on so investor/press demos
  // showcase the hands-free flow without the founder having to
  // remember to tap the mic. Override is in-memory only — the user's
  // saved pref is preserved.
  const { active: demoActive } = useDemoMode();
  const effectiveVoiceEnabled = voiceEnabled || demoActive;

  const voice = useVoiceCommands({
    enabled: effectiveVoiceEnabled,
    ttsActive: tts.speaking,
    onCommand: (intent) => {
      track("voice_command", { intent });
      switch (intent) {
        case "next": goNext(); break;
        case "previous": goPrev(); break;
        case "done": toggleDone(); break;
        case "snap": captureSnapshot(); break;
        case "exit":
          tts.stop();
          onClose?.();
          break;
        default: break;
      }
    },
  });

  // Flash the recognized wake/intent for ~1.6s. Driven by the hook's
  // `lastIntent` so the wake state ("listening for command…") shows
  // up too, not just dispatched commands.
  useEffect(() => {
    if (!voice.lastIntent || !effectiveVoiceEnabled) return;
    setVoiceFlash(voice.lastIntent);
    const t = setTimeout(() => setVoiceFlash(null), 1800);
    return () => clearTimeout(t);
  }, [voice.lastIntent, effectiveVoiceEnabled]);

  // P5 funnel: fire `coaching_tip_shown` on every step entry,
  // independent of TTS/camera state. The tip is shown VISUALLY in
  // LiveCoachStepCard regardless of phase — even in text-only
  // fallback mode the user reads it on screen. Splitting this from
  // the TTS-gated effect above ensures the funnel count matches
  // what users actually saw, not just what they heard.
  useEffect(() => {
    if (!step) return;
    trackFP("coaching_tip_shown", {
      step_idx: stepIdx,
      category: step?.category || "unknown",
      text_only: phase === "text_only",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, step?.category, phase]);


  // Surface mic-permission denial as a visible toast — otherwise the
  // mic icon just sits there inert and the user has no idea why
  // saying "Hey Trouvé" does nothing. Fires once per session, only
  // when the user has actively opted into voice.
  const permissionToastRef = useRef(false);
  useEffect(() => {
    if (
      effectiveVoiceEnabled &&
      voice.permissionDenied &&
      !permissionToastRef.current
    ) {
      permissionToastRef.current = true;
      toast.error(
        "Trouvé needs mic access for voice commands. Enable it in your browser settings, then tap the mic again.",
        { duration: 6000 },
      );
      // Auto-turn off the toggle so the UI state matches reality.
      setVoiceEnabled(false);
      try {
        if (ephemeral) return;
        window.localStorage.setItem(VOICE_PREF_KEY, "0");
      } catch {
        /* no-op */
      }
    }
  }, [voice.permissionDenied, effectiveVoiceEnabled, ephemeral]);
  const toggleVoice = () => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    if (!ephemeral) {
      try { window.localStorage.setItem(VOICE_PREF_KEY, next ? "1" : "0"); } catch { /* no-op */ }
    }
    track("voice_commands_toggled", { enabled: next });

    // First-time voice tutorial: when the user opts in for the very
    // first time AND has TTS enabled, Trouvé teaches the command set
    // out loud. Versioned localStorage key so we can refresh the
    // tutorial copy later without forcing it on returning users.
    const TUTORIAL_KEY = "trubeaute.trouve.voice_tutorial_played.v1";
    if (!ephemeral && next && tts.enabled) {
      try {
        if (
          typeof window !== "undefined" &&
          window.localStorage &&
          !window.localStorage.getItem(TUTORIAL_KEY)
        ) {
          // Tiny delay so the mic permission prompt + browser UX
          // settles before Trouvé starts speaking. Otherwise the
          // tutorial overlaps with the prompt and gets cut off.
          setTimeout(() => {
            tts.speak(
              "Okay gorgeous, you've got me hands-free now. " +
                "Try saying: next. Or back. Or done. Or snap. " +
                "Or exit when you're finished. I'm listening.",
            );
            try { window.localStorage.setItem(TUTORIAL_KEY, "1"); } catch { /* no-op */ }
            track("trouve_voice_tutorial_played");
          }, 600);
        }
      } catch {
        /* localStorage unavailable — silently skip */
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[60] bg-[#1a1416]"
      data-testid="live-coach-root"
    >
      {/* Camera + canvas overlay */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          muted
          playsInline
          className="w-full h-full object-cover"
          style={{ transform: "scaleX(-1)" }}
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-300"
          style={{
            transform: "scaleX(-1)",
            opacity: 1,
          }}
        />
        {/* Vignette so UI text reads well */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/70" />
      </div>

      {/* First-open on-mirror privacy reassurance — Apple App Review
          loves these (camera-using apps must clearly explain data
          handling near the actual capture moment). Auto-dismisses
          after 4 seconds and is suppressed on subsequent opens via
          localStorage. Keeps the drag-mom voice. */}
      {phase === "ready" && <PrivacyToast ephemeral={ephemeral} />}

      {/* Top bar */}
      <LiveCoachTopBar
        tts={tts}
        voice={voice}
        effectiveVoiceEnabled={effectiveVoiceEnabled}
        onToggleVoice={toggleVoice}
        onClose={() => {
          tts.stop();
          onClose?.();
        }}
      />

      {/* First-open wake-word nudge — sits below the top bar, tail
          pointing at the mic toggle. Auto-dismisses after 8s and is
          remembered via localStorage so it only shows once. Hidden
          when the browser doesn't support SpeechRecognition. */}
      {!ephemeral && <MirrorWakeWordHint
        voiceSupported={voice.supported}
        voiceEnabled={effectiveVoiceEnabled}
      />}

      {/* Shutter is rendered inside the coach-card container below */}

      {/* Face-shape badge — only shown during the contour step once
          the classifier has locked on. Tapping opens the calibrator
          so users can confirm or override the detection. */}
      {!ephemeral && step?.category === "contour" && (
        <LiveCoachFaceShapeBadge
          faceShape={faceShape}
          onClick={() => setCalibratorOpen(true)}
        />
      )}

      {!ephemeral && <FaceShapeCalibrator
        open={calibratorOpen}
        currentShape={faceShape}
        onClose={() => setCalibratorOpen(false)}
        onSaved={(res) => {
          if (res?.shape) {
            faceShapeRef.current = res.shape;
            faceShapeSourceRef.current = res.source || "user_set";
            setFaceShape(res.shape);
            setFaceShapeSource(res.source || "user_set");
          }
        }}
        theme="mirror"
      />}

      {/* Voice-command flash — confirms the wake word was heard or a
          command fired. Auto-dismisses after 1.8s via timer in the
          useEffect that syncs voice.lastIntent → voiceFlash. */}
      {effectiveVoiceEnabled && (
        <LiveCoachVoiceFlash flash={voiceFlash} />
      )}

      {/* Task 6 — Trouvé positive reinforcement. Pops a 2.5s
          "Nice." / "We're getting somewhere." pill on alternate step
          completions (counter % 2 === 0) plus the final step. If TTS
          is on, also speaks the line aloud through the same Nova
          voice that narrates routine cues. */}
      <LiveCoachEncouragement
        messageKey={encouragementKey}
        onDismiss={() => setEncouragementKey(null)}
        tts={tts}
      />

      {/* Soft shutter flash */}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.85 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="absolute inset-0 z-30 pointer-events-none bg-white"
          />
        )}
      </AnimatePresence>

      {/* Loading / error states */}
      <LiveCoachStatusOverlay
        phase={phase}
        loadingStage={loadingStage}
        errorMsg={errorMsg}
        onClose={onClose}
        onRetry={() => {
          setErrorMsg("");
          setLoadingStage("model");
          setPhase("loading");
          setInitAttempt((attempt) => attempt + 1);
        }}
        onContinueTextOnly={() => {
          // P5: graceful fallback when camera / MediaPipe / GPU init
          // failed. We set phase="text_only" which:
          //   • hides the LiveCoachStatusOverlay (dismissed)
          //   • LETS the LiveCoachStepCard at the bottom keep rendering
          //     (it renders unconditionally already)
          //   • The video element still mounts but has no stream, so
          //     it's just a black backdrop — UI is still readable.
          // Stops the TTS warm-up (no point speaking against silence)
          // but still emits coaching_tip_shown for the funnel.
          setPhase("text_only");
        }}
      />

      {/* Bottom step coach card */}
      <LiveCoachStepCard
        step={step}
        steps={steps}
        stepIdx={stepIdx}
        zone={zone}
        doneSet={doneSet}
        onMarkDone={toggleDone}
        onPrev={goPrev}
        onNext={goNext}
        onFinish={() => {
          // Mark the final step done before closing — otherwise the last
          // lip step never checks off in the routine view because Finish
          // skipped the onStepDone callback that goNext fires for every
          // other step.
          setDoneSet((prev) => new Set([...prev, stepIdx]));
          onStepDone?.(stepIdx);
          track("learn_with_trouve_completed", { total_steps: steps.length });
          // P5 funnel: final completion event. Captures the % of users
          // who hit `live_coach_started` AND made it all the way through.
          trackFP("live_coach_completed", {
            total_steps: steps.length,
            text_only: phase === "text_only",
          });
          tts.stop();
          onClose?.();
        }}
        onCapture={captureSnapshot}
        capturing={savingSnap}
        showShutter={phase === "ready" && !!routineId}
      />
      {/* Persistent "Ask Trouvé" floating button — gated server-side
          to Pro · ambassador · ambassador-referred users. Hides itself for
          free users with no access so we don't tease them.
          P5: showAskTrouveAlways=true on /live-coach-demo so guest
          visitors can also reach the GPT-vision escalation path. */}
      {!ephemeral && <AskTrouveOverlay forceVisible={showAskTrouveAlways} />}
    </motion.div>
  );
};

export default LiveCoach;
